/**
 * SlideConfigStep.tsx
 *
 * Build Bar — Step 2: Diapositivas.
 *
 * A pseudo-PPTX editor where users can view, reorder, edit, and manage
 * the slide configuration list produced by the AI plan step.
 *
 * Layout (NO page scroll — panels scroll internally):
 *
 *   ┌─ Toolbar ───────────────────────────────────────────────────────┐
 *   │ [Cambiar plantilla]  Plantilla: {name}  [+ Agregar]  N slides  │
 *   └─────────────────────────────────────────────────────────────────┘
 *   ┌─ Sidebar (200px) ─────┐ ┌─ Detail panel (flex 1) ─────────────┐
 *   │ Scrollable slide list │ │ SlideDetailPanel for selected slide  │
 *   │ Click → select        │ │ (scrollable, large preview + fields) │
 *   │ Drag to reorder       │ │                                      │
 *   └───────────────────────┘ └──────────────────────────────────────┘
 *   ┌─ Footer row ────────────────────────────────────────────────────┐
 *   │                                  [Continuar a imágenes →]      │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * On mount:
 *  - If `slides` in store is empty, calls `useGenerateSlides` automatically.
 *  - While loading: sidebar shows 10 rect skeletons; detail shows a large
 *    rect + 3 text skeletons.
 *
 * Features:
 *  - Left sidebar: click to select, drag-and-drop to reorder (HTML5 DnD).
 *  - Detail panel: editable fields via SlideDetailPanel; all changes are
 *    immediately committed to the store and debounced-saved to the backend.
 *  - "+ Agregar": appends a new hero_cover slide with empty fields.
 *  - "Continuar a imágenes": advances to Build step 3.
 *  - Auto-save: 500ms debounce on any slide mutation via `useSaveSlides`.
 */

import { useEffect, useRef, useCallback, useState, type MouseEvent } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import { useGenerateSlides, useSaveSlides } from '@/api/useWizardV2';
import { usePlantillas } from '@/api/usePresentationWizard';
import type { SlideConfig } from '@/types/wizardV2';
import { buildDefaultDeck } from '@/constants/defaultDeck';
import { SlideMockPreview } from './SlideMockPreview';
import { SlideDetailPanel } from './SlideDetailPanel';
import { SlideLightbox } from './SlideLightbox';
import styles from './SlideConfigStep.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Debounce delay (ms) before auto-saving slide changes to the backend. */
const AUTO_SAVE_DELAY_MS = 500;

/** Number of skeleton cards shown in the sidebar while slides are loading. */
const SIDEBAR_SKELETON_COUNT = 10;

const SLIDE_MESSAGES = [
  'Generating 50+ slide framework...',
  'Mapping your event journey...',
  'Crafting section narratives...',
  'Arranging content hierarchy...',
  'Defining visual components...',
  'Placing cost tables and stats...',
  'Polishing slide flow...',
];

const SLIDE_MSG_INTERVAL_MS = 7_500;

/** Default component type for newly added slides. */
const DEFAULT_NEW_COMPONENT = 'hero_section';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SlideConfigStep — Build bar step 2.
 *
 * Reads and writes slide state exclusively via `useWizardStore`.
 * TanStack Query mutations (useGenerateSlides, useSaveSlides) are the only
 * server-communication paths; results are committed to the store on success.
 */
export const SlideConfigStep = () => {
  // ── Store ──────────────────────────────────────────────────────────────────

  const {
    presentationId,
    formData,
    slides,
    selectedSlideIndex,
    setSlides,
    reorderSlides,
    updateSlide,
    removeSlide,
    setSelectedSlideIndex,
    goToBuildStep,
    returnToSetup,
    goToSetupStep,
  } = useWizardStore();

  // ── Catalog data ───────────────────────────────────────────────────────────

  const { data: plantillas } = usePlantillas();
  const selectedPlantilla = plantillas?.find((p) => p.slug === formData.plantilla_slug);

  // ── API mutations ──────────────────────────────────────────────────────────

  const generateSlides = useGenerateSlides();
  const saveSlides = useSaveSlides();

  // ── Auto-save debounce ref ─────────────────────────────────────────────────

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule a debounced save of the current slides array.
   * Cancels any previous timer before scheduling a new one.
   * Called after every slide mutation (update, reorder, add, remove).
   */
  const scheduleAutoSave = useCallback(
    (currentSlides: SlideConfig[]) => {
      if (!presentationId) return;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        saveSlides.mutate(
          { id: presentationId, slides: currentSlides },
          {
            onError: () => {
              toast.error('Could not save changes. Please try again.', { duration: 8_000 });
            },
          },
        );
      }, AUTO_SAVE_DELAY_MS);
    },
    [presentationId, saveSlides],
  );

  /** Cancel the auto-save timer on unmount to prevent state updates on dead components. */
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // ── Auto-generate slides on mount ──────────────────────────────────────────

  /**
   * When the user first arrives at this step and no slides have been generated
   * yet, trigger AI slide generation automatically. Guarded by `slides.length`
   * so repeated step visits do not re-generate.
   */
  useEffect(() => {
    if (slides.length === 0 && !generateSlides.isPending && presentationId) {
      generateSlides.mutate(
        { id: presentationId },
        {
          onSuccess: (result) => {
            setSlides(result);
            if (result.length > 0) setSelectedSlideIndex(0);
            toast.success('Slides generated successfully.', { duration: 3_000 });
          },
          onError: (err) => {
            toast.error(extractApiError(err, 'Error generating slides.'), { duration: 8_000 });
            // Fallback: scaffold a complete deck locally so the user can
            // still work even when the backend endpoint is unavailable.
            const fallback = buildDefaultDeck(
              formData.event_name || 'Corporate Event',
              formData.client_name || '',
              formData.venue,
            );
            setSlides(fallback);
            if (fallback.length > 0) setSelectedSlideIndex(0);
            toast('Base structure created. You can edit each slide.', {
              duration: 5_000,
              icon: '📋',
            });
          },
        },
      );
    }
    // We intentionally only run on mount — guarded by slides.length.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-select first slide when slides load ───────────────────────────────

  useEffect(() => {
    if (slides.length > 0 && selectedSlideIndex === null) {
      setSelectedSlideIndex(0);
    }
  }, [slides.length, selectedSlideIndex, setSelectedSlideIndex]);

  // ── Slide mutation handlers ────────────────────────────────────────────────

  /**
   * Update a single slide's fields and schedule an auto-save.
   */
  const handleUpdate = useCallback(
    (index: number, partial: Partial<SlideConfig>) => {
      updateSlide(index, partial);
      // Compute next slides array to pass to scheduleAutoSave — store update
      // is synchronous so we can derive the next state inline.
      const nextSlides = slides.map((sl, i) => (i === index ? { ...sl, ...partial } : sl));
      scheduleAutoSave(nextSlides);
    },
    [updateSlide, slides, scheduleAutoSave],
  );

  /**
   * Remove a slide and schedule an auto-save.
   * Guarded: does nothing if only 1 slide remains (store already guards this,
   * but this prevents unnecessary calls).
   */
  const handleRemove = useCallback(
    (index: number) => {
      if (slides.length <= 1) return;
      removeSlide(index);
      const nextSlides = slides.filter((_, i) => i !== index);
      scheduleAutoSave(nextSlides);
    },
    [removeSlide, slides, scheduleAutoSave],
  );

  /**
   * Append a new slide with default values and select it.
   */
  const handleAddSlide = useCallback(() => {
    const newSlide: SlideConfig = {
      component: DEFAULT_NEW_COMPONENT,
      section: 'New section',
      title: 'New slide',
      subtitle: '',
      blocks: [],
      background: { type: 'plantilla_color', plantilla_index: 0 },
      images: [],
    };
    const nextSlides = [...slides, newSlide];
    setSlides(nextSlides);
    setSelectedSlideIndex(nextSlides.length - 1);
    scheduleAutoSave(nextSlides);
  }, [slides, setSlides, setSelectedSlideIndex, scheduleAutoSave]);

  // ── Context menu state (right-click duplicate) ────────────────────────────

  /**
   * Position and target for the right-click context menu.
   * `null` means the menu is closed.
   */
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    slideIndex: number;
  } | null>(null);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);

  /** Close the context menu on click-outside, Escape key, or scroll. */
  useEffect(() => {
    if (!contextMenu) return;

    const closeMenu = () => setContextMenu(null);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };

    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('scroll', closeMenu, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('scroll', closeMenu, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  /**
   * Open the context menu for a slide card on right-click.
   * Prevents the default browser context menu.
   */
  const handleContextMenu = useCallback(
    (e: MouseEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, slideIndex: index });
    },
    [],
  );

  /**
   * Insert a deep copy of the slide at `sourceIndex` right after it.
   * Selects the new duplicate.
   */
  const handleDuplicateSlide = useCallback(
    (sourceIndex: number) => {
      const source = slides[sourceIndex];
      if (!source) return;
      // Deep copy via JSON round-trip to detach all references.
      const duplicate: SlideConfig = JSON.parse(JSON.stringify(source));
      const nextSlides = [
        ...slides.slice(0, sourceIndex + 1),
        duplicate,
        ...slides.slice(sourceIndex + 1),
      ];
      setSlides(nextSlides);
      setSelectedSlideIndex(sourceIndex + 1);
      scheduleAutoSave(nextSlides);
      setContextMenu(null);
    },
    [slides, setSlides, setSelectedSlideIndex, scheduleAutoSave],
  );

  /**
   * Insert a blank default slide at the given position index.
   * Slides from `atIndex` onward shift down by one.
   * Selects the newly inserted slide.
   */
  const handleInsertSlideAt = useCallback(
    (atIndex: number) => {
      const newSlide: SlideConfig = {
        component: DEFAULT_NEW_COMPONENT,
        section: 'New section',
        title: 'New slide',
        subtitle: '',
        blocks: [],
        background: { type: 'plantilla_color', plantilla_index: 0 },
        images: [],
      };
      const nextSlides = [
        ...slides.slice(0, atIndex),
        newSlide,
        ...slides.slice(atIndex),
      ];
      setSlides(nextSlides);
      setSelectedSlideIndex(atIndex);
      scheduleAutoSave(nextSlides);
    },
    [slides, setSlides, setSelectedSlideIndex, scheduleAutoSave],
  );

  // ── HTML5 Drag-and-Drop reorder ────────────────────────────────────────────

  /**
   * Track the index being dragged so `onDrop` can compute the move.
   * Using a ref (not state) prevents re-renders during drag.
   */
  const dragFromIndex = useRef<number | null>(null);

  /** Track the current hover target for the drop-indicator border. */
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    dragFromIndex.current = index;
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault(); // Allow drop
      setDragOverIndex(index);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, toIndex: number) => {
      e.preventDefault();
      setDragOverIndex(null);
      const fromIndex = dragFromIndex.current;
      if (fromIndex === null || fromIndex === toIndex) return;
      reorderSlides(fromIndex, toIndex);
      // Derive next slides for auto-save.
      const next = [...slides];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      scheduleAutoSave(next);
      dragFromIndex.current = null;
    },
    [reorderSlides, slides, scheduleAutoSave],
  );

  const handleDragEnd = useCallback(() => {
    dragFromIndex.current = null;
    setDragOverIndex(null);
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────

  const isLoading = generateSlides.isPending;
  const selectedSlide =
    selectedSlideIndex !== null ? slides[selectedSlideIndex] ?? null : null;

  // ── Animated messages while slides generate (~60s) ─────────────────────────

  const [slideMsg, setSlideMsg] = useState(0);
  const [slideElapsed, setSlideElapsed] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setSlideMsg(0);
      setSlideElapsed(0);
      return;
    }
    const msgId = setInterval(
      () => setSlideMsg((s) => (s + 1) % SLIDE_MESSAGES.length),
      SLIDE_MSG_INTERVAL_MS,
    );
    const tickId = setInterval(() => setSlideElapsed((s) => s + 1), 1_000);
    return () => {
      clearInterval(msgId);
      clearInterval(tickId);
    };
  }, [isLoading]);

  /**
   * Resolve the correct plantilla background URL for a given slide.
   *
   * Uses `slide.background.plantilla_index` when it maps to a valid
   * background entry. When all slides default to index 0 (common before
   * the backend assigns proper indices), falls back to cycling through
   * available backgrounds based on the slide's position and component
   * type — hero_* components prefer `hero_only` backgrounds, others use
   * `any` backgrounds in round-robin order.
   *
   * @param slide - The SlideConfig whose background we want to resolve.
   * @param slideIndex - Position in the slides array (for round-robin).
   * @returns The absolute GCS URL string, or `undefined` if no plantilla
   *   is selected.
   */
  const getPlantillaBgUrl = useCallback(
    (slide: SlideConfig, slideIndex: number): string | undefined => {
      if (!selectedPlantilla) return undefined;
      const bgs = selectedPlantilla.backgrounds;
      if (!bgs || bgs.length === 0) return selectedPlantilla.preview_image;

      const idx = slide.background?.plantilla_index ?? 0;
      const exactMatch = bgs.find((b) => b.index === idx);

      // If the slide has a non-zero explicit index that matches, use it.
      if (idx !== 0 && exactMatch) return exactMatch.url;

      // Otherwise cycle through backgrounds by component type + position.
      const isHero = slide.component.startsWith('hero_');
      const heroBgs = bgs.filter((b) => b.usage === 'hero_only' || b.usage === 'any');
      const anyBgs = bgs.filter((b) => b.usage !== 'hero_only');
      const pool = isHero ? (heroBgs.length > 0 ? heroBgs : bgs) : (anyBgs.length > 0 ? anyBgs : bgs);

      return pool[slideIndex % pool.length]?.url ?? bgs[0]?.url ?? selectedPlantilla.preview_image;
    },
    [selectedPlantilla],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className={styles.toolbar} role="toolbar" aria-label="Slide options">
        <div className={styles.toolbarPlantilla}>
          <span className={styles.toolbarPlantillaLabel}>Template:</span>
          <span className={styles.toolbarPlantillaName}>
            {selectedPlantilla?.name ?? formData.plantilla_slug ?? '—'}
          </span>
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            returnToSetup();
            goToSetupStep(2);
          }}
          aria-label="Go back to template selection"
        >
          Change template
        </Button>

        <Button
          variant="success"
          size="sm"
          onClick={handleAddSlide}
          disabled={isLoading}
          aria-label="Add slide"
        >
          + Add
        </Button>

        <span className={styles.slideCount} aria-live="polite" aria-atomic>
          {isLoading ? 'Generating...' : `${slides.length} slides`}
        </span>
      </div>

      {/* ── Editor area (sidebar + detail panel) ─────────────────────────── */}
      <div className={styles.editorArea}>
        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <div className={styles.sidebar} aria-label="Slide list">
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarHeaderTitle}>Slides</h2>
          </div>

          {isLoading ? (
            /* Loading: animated sidebar with progress indicator */
            <div className={styles.sidebarLoadingList} aria-label="Loading slides..." aria-busy role="status">
              {Array.from({ length: SIDEBAR_SKELETON_COUNT }, (_, i) => (
                <div
                  key={i}
                  className={styles.sidebarSkeletonBar}
                  style={{ animationDelay: `${i * 0.12}s` }}
                />
              ))}
              <div className={styles.sidebarLoadingTimer}>
                {Math.floor(slideElapsed / 60).toString().padStart(2, '0')}:{(slideElapsed % 60).toString().padStart(2, '0')}
              </div>
            </div>
          ) : slides.length === 0 ? (
            /* Empty state */
            <div className={styles.emptyState}>
              <p className={styles.emptyStateText}>
                No slides yet. Use "+ Add" to get started.
              </p>
            </div>
          ) : (
            /* Slide thumbnail list */
            <div className={styles.slideList} role="list">
              {/* Insert gap at the very top (position 0) */}
              <div className={styles.addSlotGap} aria-hidden>
                <button
                  type="button"
                  className={styles.addSlotBtn}
                  onClick={() => handleInsertSlideAt(0)}
                  title="Insert slide here"
                  aria-label="Insert slide at the beginning"
                >
                  +
                </button>
              </div>

              {slides.map((slide, i) => (
                <div key={i} className={styles.slideCardWrapper}>
                  {/* Slide card */}
                  <div
                    role="listitem"
                    className={[
                      styles.slideCard,
                      selectedSlideIndex === i ? styles.slideCardSelected : '',
                      dragOverIndex === i ? styles.slideCardDragOver : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    draggable
                    onClick={() => setSelectedSlideIndex(i)}
                    onContextMenu={(e) => handleContextMenu(e, i)}
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, i)}
                    onDragEnd={handleDragEnd}
                    aria-label={`Slide ${i + 1}: ${slide.title || slide.component}`}
                    aria-selected={selectedSlideIndex === i}
                  >
                    <SlideMockPreview
                      component={slide.component}
                      plantillaBgUrl={getPlantillaBgUrl(slide, i)}
                      title={slide.title}
                      section={slide.section}
                      slideNumber={i + 1}
                      isSelected={selectedSlideIndex === i}
                    />
                  </div>

                  {/* Insert gap after each slide */}
                  <div className={styles.addSlotGap} aria-hidden>
                    <button
                      type="button"
                      className={styles.addSlotBtn}
                      onClick={() => handleInsertSlideAt(i + 1)}
                      title="Insert slide here"
                      aria-label={`Insert slide after slide ${i + 1}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Center-right detail area ──────────────────────────────────── */}
        <div className={styles.detailArea} aria-label="Slide detail">
          {isLoading ? (
            /* Loading: animated message card */
            <div className={styles.detailLoadingCard} aria-label="Generating slides..." aria-busy role="status">
              <div className={styles.detailLoadingShimmer} aria-hidden />
              <div className={styles.detailLoadingIcon} aria-hidden>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <rect x="4" y="4" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2" strokeDasharray="6 3" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="8s" repeatCount="indefinite" />
                  </rect>
                  <rect x="10" y="10" width="20" height="3" rx="1.5" fill="currentColor" opacity="0.5" />
                  <rect x="10" y="16" width="16" height="3" rx="1.5" fill="currentColor" opacity="0.35" />
                  <rect x="10" y="22" width="18" height="3" rx="1.5" fill="currentColor" opacity="0.25" />
                </svg>
              </div>
              <p className={styles.detailLoadingMessage} key={slideMsg}>
                {SLIDE_MESSAGES[slideMsg]}
              </p>
              <p className={styles.detailLoadingHint}>This usually takes about 60 seconds.</p>
              <div className={styles.detailProgressBar}>
                <div className={styles.detailProgressIndeterminate} />
              </div>
            </div>
          ) : selectedSlide !== null && selectedSlideIndex !== null ? (
            /* Loaded: detail panel for the selected slide */
            <SlideDetailPanel
              key={selectedSlideIndex}
              slide={selectedSlide}
              slideIndex={selectedSlideIndex}
              totalSlides={slides.length}
              plantillaBgUrl={getPlantillaBgUrl(selectedSlide, selectedSlideIndex)}
              availableBackgrounds={selectedPlantilla?.backgrounds}
              onUpdate={handleUpdate}
              onRemove={setConfirmDeleteIndex}
              onPrev={() => setSelectedSlideIndex(selectedSlideIndex - 1)}
              onNext={() => setSelectedSlideIndex(selectedSlideIndex + 1)}
              hasPrev={selectedSlideIndex > 0}
              hasNext={selectedSlideIndex < slides.length - 1}
            />
          ) : (
            /* No slide selected */
            <div className={styles.detailEmpty}>
              <p>Select a slide from the list to edit it.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer: advance to next step ─────────────────────────────────── */}
      <div className={styles.footerRow}>
        <Button
          variant="primary"
          size="md"
          onClick={() => goToBuildStep(4)}
          disabled={isLoading || slides.length === 0}
          aria-label="Continue to the images step"
        >
          Continue to images &#8594;
        </Button>
      </div>

      {/* ── Right-click context menu ──────────────────────────────────────── */}
      {contextMenu !== null && (
        <div
          className={styles.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          role="menu"
          aria-label="Slide options"
          /* Stop the mousedown close-listener from firing on the menu itself. */
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className={styles.contextMenuItem}
            onClick={() => {
              setLightboxIndex(contextMenu.slideIndex);
              setContextMenu(null);
            }}
          >
            View macro
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.contextMenuItem}
            onClick={() => handleDuplicateSlide(contextMenu.slideIndex)}
          >
            Duplicate slide
          </button>
          <button
            type="button"
            role="menuitem"
            className={`${styles.contextMenuItem} ${styles.contextMenuItemDanger}`}
            onClick={() => {
              setConfirmDeleteIndex(contextMenu.slideIndex);
              setContextMenu(null);
            }}
            disabled={slides.length <= 1}
          >
            Delete slide
          </button>
        </div>
      )}

      {/* ── Confirm delete modal ─────────────────────────────────────────── */}
      {confirmDeleteIndex !== null && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setConfirmDeleteIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Confirm deletion"
        >
          <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
            <p className={styles.confirmText}>
              Delete slide {confirmDeleteIndex + 1}?
            </p>
            <p className={styles.confirmSubtext}>
              This action cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancelBtn}
                onClick={() => setConfirmDeleteIndex(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDeleteBtn}
                onClick={() => {
                  handleRemove(confirmDeleteIndex);
                  setConfirmDeleteIndex(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <SlideLightbox
        slides={slides}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        getPlantillaBgUrl={getPlantillaBgUrl}
      />
    </div>
  );
};
